import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Home = () => {
	const [isHovered, setIsHovered] = useState(-1);
	const [showPasswordPopup, setShowPasswordPopup] = useState(false);
	const [showIncorrectPasswordPopup, setShowIncorrectPasswordPopup] =
		useState(false);
	const [password, setPassword] = useState("");
	const [siriusRotation, setSiriusRotation] = useState(-1);
	const [georgiaRotation, setGeorgiaRotation] = useState(1);
	const navigate = useNavigate();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const images = [
			"blossom.png",
			"blossom-2.png",
			"sas.png",
			"photography.jpg",
			"photography-2.jpg",
			"photography-3.jpg",
			"about.png",
		];

		images.forEach((imageName) => {
			const img = new Image();
			img.src = require(`../assets/home-images/${imageName}`);
		});
	}, []);

	const createRandomFloatingVariants = (index) => ({
		initial: { opacity: 0, scale: 0.95 },
		animate: {
			opacity: 1,
			scale: 1,
			x: [0, 8 + index * 2, 0, -8 - index * 2, 0],
			y: [0, 8 + index * 3, 0, -8 - index * 3, 0],
			rotate: [0, 1, 0, -1, 0],
			transition: {
				opacity: { duration: 0.3 },
				scale: { duration: 0.3 },
				x: {
					duration: 6 + index,
					ease: "easeInOut",
					repeat: Infinity,
					times: [0, 0.25, 0.5, 0.75, 1],
				},
				y: {
					duration: 6 + index * 1.5,
					ease: "easeInOut",
					repeat: Infinity,
					times: [0, 0.25, 0.5, 0.75, 1],
				},
				rotate: {
					duration: 8 + index * 2,
					ease: "easeInOut",
					repeat: Infinity,
					times: [0, 0.25, 0.5, 0.75, 1],
				},
			},
		},
		exit: {
			opacity: 0,
			scale: 0.95,
			transition: { duration: 0.2 },
		},
	});

	const handleSASClick = (e) => {
		e.preventDefault();
		setShowPasswordPopup(true);
	};

	const handlePasswordSubmit = (e) => {
		e.preventDefault();
		if (
			password.trim() === process.env.REACT_APP_SAS_PASSWORD_SEP24 ||
			password.trim() === process.env.NEXT_PUBLIC_SAS_PASSWORD_SEP24
		) {
			navigate("/sas", { state: { isAuthenticated: true } });
			setShowPasswordPopup(false);
		} else {
			setShowIncorrectPasswordPopup(true);
		}
		setPassword("");
	};

	// LinkButton component for card links with 3D look only on hover + tracking light effect
	const LinkButton = ({
		to,
		onMouseEnter,
		onMouseLeave,
		onClick,
		className = "",
		children,
	}) => {
		const [isBtnHovered, setIsBtnHovered] = useState(false);
		const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
		const btnRef = useRef(null);

		const handleMouseMove = (e) => {
			const rect = btnRef.current.getBoundingClientRect();
			setMousePos({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			});
		};

		return (
			<Link
				ref={btnRef}
				to={to}
				className={`relative overflow-hidden card flex p-4 justify-between items-center rounded-xl font-semibold text-black bg-transparent border-none shadow-none text-base transition-all duration-150 ease-in-out hover:bg-gradient-to-b hover:from-white/0 hover:via-white/90 hover:to-white hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] hover:border hover:border-accent/20 hover:bg-neutral-50 active:bg-neutral-200 ${className}`}
				onMouseEnter={(e) => {
					setIsBtnHovered(true);
					onMouseEnter && onMouseEnter(e);
				}}
				onMouseLeave={(e) => {
					setIsBtnHovered(false);
					onMouseLeave && onMouseLeave(e);
				}}
				onMouseMove={handleMouseMove}
				onClick={onClick}
				style={{ position: "relative" }}
			>
				{/* Tracking light effect */}
				{isBtnHovered && (
					<span
						className="pointer-events-none absolute z-20"
						style={{
							left: mousePos.x - 80,
							top: mousePos.y - 80,
							width: 160,
							height: 160,
							background:
								"radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.18) 60%, transparent 100%)",
							filter: "blur(8px)",
							transition: "left 0.08s, top 0.08s",
							borderRadius: "50%",
							mixBlendMode: "lighten",
							pointerEvents: "none",
						}}
					/>
				)}
				{children}
			</Link>
		);
	};

	// FloatingImageSet component for animated hover images
	const FloatingImageSet = ({ images, hoveredIndex, count }) => {
		// Array of positions for up to 3 images (responsive design)
		const positions = [
			{
				className: "z-10",
				style: {
					maxWidth: "clamp(180px, 60vw, 80%)",
					maxHeight: "clamp(135px, 50vh, 80%)",
				},
				rounded: "rounded-xl md:rounded-2xl lg:rounded-3xl",
			}, // main
			{
				className: `absolute left-1 top-6 md:left-4 md:top-2 lg:left-2 lg:top-10 z-${
					count < 3 ? 30 : 0
				}`,
				style: {
					maxWidth: "clamp(70px, 22vw, 38%)",
					maxHeight: "clamp(52px, 18vh, 38%)",
				},
				rounded: "rounded-lg md:rounded-xl lg:rounded-2xl",
			}, // overlap 1
			{
				className:
					"absolute right-1 bottom-4 md:right-4 md:bottom-2 lg:right-2 lg:bottom-6 z-20",
				style: {
					maxWidth: "clamp(70px, 22vw, 38%)",
					maxHeight: "clamp(52px, 18vh, 38%)",
				},
				rounded: "rounded-lg md:rounded-xl lg:rounded-2xl",
			}, // overlap 2
		];

		return (
			<div className="relative w-full h-full flex items-center justify-center">
				{[...Array(count)].map((_, i) => (
					<motion.div
						key={i}
						className={`bg-transparent shadow-[0_0_40px_10px_rgba(0,0,0,0.25)] overflow-hidden ${
							positions[i]?.className || "absolute"
						} ${positions[i]?.rounded || "rounded-2xl"}`}
						style={positions[i]?.style || { maxWidth: "38%", maxHeight: "38%" }}
						initial="initial"
						animate="animate"
						exit="exit"
						variants={createRandomFloatingVariants(hoveredIndex + i * 10)}
					>
						<img
							src={require(`../assets/home-images/${
								images[i % images.length]
							}`)}
							alt={`Floating${i}`}
							className={`w-full h-full object-cover ${
								positions[i]?.rounded || "rounded-2xl"
							}`}
						/>
					</motion.div>
				))}
			</div>
		);
	};

	// New component for rotatable span
	const RotatableSpan = ({
		children,
		baseRotation,
		className,
		onRotationChange,
	}) => {
		const spanRef = useRef(null);

		const handleMouseMove = (e) => {
			if (!spanRef.current) return;

			const rect = spanRef.current.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			// Calculate distance from center
			const mouseX = e.clientX - centerX;
			const mouseY = e.clientY - centerY;

			// Calculate additional rotation based on mouse position
			// The further from center, the more rotation (max ±3 degrees)
			const maxAdditionalRotation = 3;
			const distance = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
			const maxDistance = 50; // pixels
			const rotationIntensity = Math.min(distance / maxDistance, 1);

			// Calculate angle based on mouse position
			const angle = Math.atan2(mouseY, mouseX);
			const additionalRotation =
				Math.sin(angle) * rotationIntensity * maxAdditionalRotation * 5;

			onRotationChange(baseRotation + additionalRotation);
		};

		const handleMouseLeave = () => {
			onRotationChange(baseRotation);
		};

		return (
			<span
				ref={spanRef}
				className={className}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				style={{
					transform: `rotate(${
						onRotationChange === setSiriusRotation
							? siriusRotation
							: georgiaRotation
					}deg)`,
					transition: "transform 0.1s ease-out",
				}}
			>
				{children}
			</span>
		);
	};

	return (
		<div className="flex flex-col lg:flex-row p-4 lg:p-20 min-h-screen bg-secondary">
			<div className="content flex flex-col w-full lg:min-w-[500px] lg:max-w-[500px] h-full justify-between">
				<div className="header flex mb-8 lg:mb-0 pt-4 pl-4">
					<p className="text-xl lg:text-2xl font-light leading-8 text-pretty">
						<span className="font-garamond text-3xl tracking-tight relative">
							Arjun Dewan
						</span>{" "}
						is a{" "}
						<span className="tracking-tight underline">
							product & design engineer
						</span>{" "}
						focused on turning ideas into prototypes.
						<br />
						<br />
						He's currently developing future automotive interfaces at{" "}
						<RotatableSpan
							baseRotation={-1}
							className="inline-block font-medium text-white bg-[#0000EF] rounded-md px-1 border border-white shadow-md align-middle select-none"
							onRotationChange={setSiriusRotation}
						>
							SiriusXM
						</RotatableSpan>{" "}
						and studying Computational Media at{" "}
						<RotatableSpan
							baseRotation={1}
							className="inline-block font-medium text-white bg-[#B3A369] rounded-md px-1 py-0.5 border border-white shadow-md align-middle select-none"
							onRotationChange={setGeorgiaRotation}
						>
							Georgia Tech
						</RotatableSpan>
						.
					</p>
				</div>
				<div className="body flex flex-col divide-y-2 divide-gray">
					<div className="work flex flex-col py-10">
						<p className="text-xl lg:text-2xl font-semibold mb-4 ml-4">Work</p>
						<div className="cards flex flex-col">
							<LinkButton
								to="/blossom"
								onMouseEnter={() => setIsHovered(0)}
								onMouseLeave={() => setIsHovered(-1)}
							>
								<div className="flex flex-col w-1/2">
									<p className="text-lg lg:text-xl font-semibold">Blossom</p>
									<p className="text-lg lg:text-xl font-light leading-5">
										AI-Native Web Browser
									</p>
								</div>
								<div className="details flex flex-col text-right w-1/2 justify-between">
									<p className="text-lg font-normal lg:text-xl text-neutral-500">
										Startup
									</p>
									<p className="text-lg font-normal lg:text-xl text-neutral-500 leading-5">
										2023 - 2025
									</p>
								</div>
							</LinkButton>
							<LinkButton
								to="/sas"
								onMouseEnter={() => setIsHovered(1)}
								onMouseLeave={() => setIsHovered(-1)}
								onClick={handleSASClick}
							>
								<div className="flex flex-col">
									<p className="text-lg lg:text-xl font-semibold">SAS</p>
									<p className="text-lg lg:text-xl font-light">
										GenAI in Design Systems
									</p>
								</div>
								<div className="details flex flex-col text-right">
									<p className="text-lg font-normal lg:text-xl text-neutral-500">
										Internship
									</p>
									<p className="text-lg font-normal lg:text-xl text-neutral-500">
										2024
									</p>
								</div>
							</LinkButton>
						</div>
					</div>
					<div className="more flex flex-col py-10">
						{/* <p className="text-2xl font-semibold mb-4 ml-4">More</p> */}
						<div className="cards flex flex-col">
							<LinkButton
								to="/photography"
								onMouseEnter={() => setIsHovered(4)}
								onMouseLeave={() => setIsHovered(-1)}
							>
								<div className="flex flex-col">
									<p className="text-lg lg:text-xl font-semibold">
										Photography
									</p>
									{/* <p className="text-xl">Web Browser for Research</p> */}
								</div>
								<div className="details flex flex-col text-right">
									{/* <p className="text-xl text-neutral-600">Concept</p>
									<p className="text-xl text-neutral-600">2023</p> */}
								</div>
							</LinkButton>
							<LinkButton
								to="/about"
								onMouseEnter={() => setIsHovered(5)}
								onMouseLeave={() => setIsHovered(-1)}
							>
								<div className="flex flex-col">
									<p className="text-lg lg:text-xl font-semibold">About</p>
									{/* <p className="text-xl">Design Direction</p> */}
								</div>
								<div className="details flex flex-col text-right">
									{/* <p className="text-xl text-neutral-600">Student Org</p>
									<p className="text-xl text-neutral-600">2024</p> */}
								</div>
							</LinkButton>
						</div>
					</div>
				</div>
			</div>
			<div className="image flex-col mt-8 lg:mt-0 lg:ml-20 w-full justify-center">
				<div className="flex w-full h-full items-center">
					{isHovered >= 0 && (
						<FloatingImageSet
							images={
								{
									0: ["blossom.png", "blossom-2.png"], // Blossom: 2 images
									1: ["sas.png"], // SAS: 1 image
									4: [
										"photography.jpg",
										"photography-2.jpg",
										"photography-3.jpg",
									], // Photography: 3 images
									5: ["about.png"], // About: 1 image
								}[isHovered] || []
							}
							hoveredIndex={isHovered}
							count={
								{
									0: 2, // Blossom
									1: 1, // SAS
									4: 3, // Photography
									5: 1, // About
								}[isHovered] || 0
							}
						/>
					)}
				</div>
			</div>
			{/* <div to="/">
				<div className="fixed bottom-4 right-4 p-8 bg-[#FF5C00] rounded-full"></div>
			</div> */}
			{showPasswordPopup && (
				<div className="fixed inset-0 flex items-center justify-center z-50">
					<div className="absolute inset-0 bg-black bg-opacity-50" />
					<div className="flex flex-col bg-white p-6 rounded-lg w-[90%] max-w-[300px] items-center z-10 relative">
						<div className="flex mb-4">
							<p className="text-xl font-semibold text-center">
								This case study is password protected.
							</p>
						</div>
						<form className="w-full" onSubmit={handlePasswordSubmit}>
							<input
								autoFocus
								type="password"
								className="flex rounded w-full px-2 py-1 mb-2 bg-neutral-100"
								placeholder="Password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								autoComplete="off"
							/>
							{showIncorrectPasswordPopup && (
								<div className="flex w-full mb-4 justify-center">
									<p className="text-red-600 bg-red-100 px-3 py-1 rounded-md">
										Incorrect password
									</p>
								</div>
							)}
							<div className="flex justify-end">
								<button
									type="button"
									className="mr-2 px-4 py-2 text-gray-600"
									onClick={() => setShowPasswordPopup(false)}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="px-8 py-2 bg-[#FF5C00] text-white rounded shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-accent/20 active:opacity-80"
								>
									Enter
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default Home;
